import React, { useEffect, useMemo, useState } from "react";
import FiltersRow from "./components/FiltersRow/FiltersRow";
import { IndicatorSelectList } from "./components/IndicatorSelectList";
import SwingTable from "./components/Table/SwingTable";
import { useSwingLibraryStore } from "./store/swingLibraryStore";
import Spinner from "../../components/shared/spinner/Spinner";
import { useSwingLibrarySettings } from "./hooks/useSwingLibrarySettings";
import { useSwingList } from "./hooks/useSwingList";
import IndicatorsRow from "./components/IndictorsRow";
import ActionButtons from "./components/ActionButtons";
import Newton from "./components/Newton/Newton";
import CollectionSelectorCard from "../compare/components/CollectionSelectorCard";
import { useUserData } from "../../hooks/useUserData";
import { useUserSubscriptionAddons } from "../../hooks/useUserSubscriptionAddons";

export function SwingLibrary() {
  const { userData } = useUserData();
  const { llmAddon } = useUserSubscriptionAddons();

  const isAdmin = userData?.role === "admin";

  const {
    activeFilters,
    resetRowSelection,
    resetSelectedVideos,
    quickCompareCollection1,
    quickCompareCollection2,
    setQuickCompareCollection1,
    setQuickCompareCollection2,
  } = useSwingLibraryStore();

  const { data: swingLibrarySettings } = useSwingLibrarySettings();

  const { isLoading } = useSwingList();

  const [isIndicatorFilterOpen, setIsIndicatorFilterOpen] = useState(false);

  const handleRemoveQuickCompareCollection1 = () => {
    setQuickCompareCollection1(null);
  };

  const handleRemoveQuickCompareCollection2 = () => {
    setQuickCompareCollection2(null);
  };

  // Reset row selection when filters or indicators change
  useEffect(() => {
    resetRowSelection();
    resetSelectedVideos();
  }, [
    activeFilters.analyzed,
    activeFilters.cameraAngle,
    activeFilters.club,
    activeFilters.date,
    activeFilters.dominantHand,
    activeFilters.tags,
    activeFilters.users,
    swingLibrarySettings?.savedIndicators,
    resetRowSelection,
    resetSelectedVideos,
  ]);

  return (
    <div className="custom-margin-top-20 w-full p-4">
      <IndicatorSelectList
        isOpen={isIndicatorFilterOpen}
        onClose={() => setIsIndicatorFilterOpen(false)}
      />

      <div className="border-gray200 sticky z-10 w-full rounded-t-xl border bg-white px-6 py-5">
        <div className="flex flex-col items-start justify-between gap-2 xl:flex-row">
          <div className="text-xl font-semibold">Swing Library</div>

          <div className="flex flex-wrap items-center gap-3 xl:justify-end">
            <ActionButtons />
          </div>
        </div>

        {quickCompareCollection1 && (
          <div className="mt-4 grid grid-cols-2 gap-6">
            <CollectionSelectorCard
              cardColor="blue"
              collection={quickCompareCollection1}
              onRemove={handleRemoveQuickCompareCollection1}
              isQuickCompare={true}
            />

            <CollectionSelectorCard
              cardColor="pink"
              collection={quickCompareCollection2}
              onRemove={handleRemoveQuickCompareCollection2}
              isQuickCompare={true}
            />
          </div>
        )}

        <div className="mt-4 flex w-full flex-wrap items-center gap-3">
          <FiltersRow />
        </div>

        <div className="border-gray200 my-4 w-full border-t border-b-0" />

        <div className="flex flex-wrap items-center gap-2 xl:flex-nowrap xl:gap-3">
          <IndicatorsRow setIsIndicatorFilterOpen={setIsIndicatorFilterOpen} />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-[50vh] items-center justify-center">
          <Spinner color="primary500" size="md" />
        </div>
      ) : (
        <SwingTable />
      )}

      {(isAdmin || llmAddon) && <Newton />}
    </div>
  );
}
